import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import { UnlinkedEntraGroupPicker } from './UnlinkedEntraGroupPicker';
import { TeamInformationFields } from './TeamInformationFields';
import { GitHubRepoPicker } from './GitHubRepoPicker';
import { RepositoryNameFields } from './RepositoryNameFields';
import { DomainPicker } from './DomainPicker';

/**
 * Field extension for the UnlinkedEntraGroupPicker component.
 * This extension allows templates to use a custom field for selecting unlinked Entra groups.
 *
 * Usage in template.yaml:
 * ```yaml
 * parameters:
 *   - title: Team Information
 *     properties:
 *       owner:
 *         title: Team Owner
 *         type: string
 *         description: Select an Entra group that doesn't have a team repository
 *         ui:field: UnlinkedEntraGroupPicker
 * ```
 *
 * @see Story 1.8.1 - Filter Unlinked Entra Groups in Team Repository Template
 */

export const UnlinkedEntraGroupPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'UnlinkedEntraGroupPicker',
    component: UnlinkedEntraGroupPicker,
  }),
);

/**
 * Field extension for the TeamInformationFields component.
 * This extension conditionally renders team information fields based on whether
 * a GitHub repository already exists for the selected Entra group.
 *
 * Usage in template.yaml:
 * ```yaml
 * parameters:
 *   - title: Team Information
 *     properties:
 *       teamInfo:
 *         type: object
 *         ui:field: TeamInformationFields
 * ```
 *
 * @see Story 1.8.1 - Environment-Independent Scaffolder
 */
export const TeamInformationFieldsExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'TeamInformationFields',
    component: TeamInformationFields,
  }),
);

/**
 * Field extension for the GitHubRepoPicker component.
 * This extension allows templates to use a dropdown that lists GitHub repositories
 * from the univerus organization, fetched live from the GitHub API.
 *
 * Supports `requestUserCredentials` to store the user's GitHub OAuth token as a
 * template secret, enabling downstream steps to authenticate as the user.
 *
 * Usage in template.yaml:
 * ```yaml
 * parameters:
 *   - title: Repository Information
 *     properties:
 *       repoName:
 *         title: Repository
 *         type: string
 *         ui:field: GitHubRepoPicker
 *         ui:options:
 *           requestUserCredentials:
 *             secretsKey: USER_OAUTH_TOKEN
 * ```
 *
 * Then reference the token in a step:
 * ```yaml
 * steps:
 *   - id: create-pr
 *     action: publish:github:pull-request
 *     input:
 *       token: ${{ secrets.USER_OAUTH_TOKEN }}
 * ```
 *
 * @see Story 2.5 — TechDocs Bootstrap Scaffolder Template
 */
export const GitHubRepoPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GitHubRepoPicker',
    component: GitHubRepoPicker,
  }),
);

export const RepositoryNameFieldsExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'RepositoryNameFields',
    component: RepositoryNameFields,
  }),
);

/**
 * Field extension for the DomainPicker component: selects a Domain entity by
 * display title (the stock EntityPicker shows the raw entity ref in its text
 * field) and writes the full entity ref string to form data.
 */
export const DomainPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'DomainPicker',
    component: DomainPicker,
  }),
);
